/**
 * Hook para gerenciar preferências do módulo de Projetos
 * Persiste no localStorage
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { ProjectsStatusFilter, ProjectsViewMode } from '../types';

const STORAGE_KEY = 'projects_preferences';

interface ProjectsPreferences {
  viewMode: ProjectsViewMode;
  statusFilter: ProjectsStatusFilter;
}

const DEFAULT_PREFERENCES: ProjectsPreferences = {
  viewMode: 'grid',
  statusFilter: 'active',
};

/**
 * Hook para gerenciar preferências do módulo de Projetos
 */
export function useProjectsPreferences() {
  const [preferences, setPreferences] = useState<ProjectsPreferences>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        return { ...DEFAULT_PREFERENCES, ...parsed };
      }
    } catch (e) {
      console.error('Error loading projects preferences:', e);
    }
    return DEFAULT_PREFERENCES;
  });

  // Skip first save (on mount)
  const isFirstRender = useRef(true);

  // Persist preferences to localStorage
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (e) {
      console.error('Error saving projects preferences:', e);
    }
  }, [preferences]);

  // Setters
  const setViewMode = useCallback((mode: ProjectsViewMode) => {
    setPreferences(prev => ({ ...prev, viewMode: mode }));
  }, []);

  const setStatusFilter = useCallback((status: ProjectsStatusFilter) => {
    setPreferences(prev => ({ ...prev, statusFilter: status }));
  }, []);

  return {
    // State
    viewMode: preferences.viewMode,
    statusFilter: preferences.statusFilter,

    // Setters
    setViewMode,
    setStatusFilter,
  };
}
